/**
 * Top-center banner for system notifications (e.g. version updates).
 * Shows when there is a new, un-dismissed notification from the WebSocket.
 * User clicks "Okay, Refresh" → code saved to localStorage, page reloads.
 */

import { AnimatePresence, motion } from 'framer-motion';
import { Info } from 'lucide-react';
import { useLanguage } from '@contexts/LanguageContext';
import type { SystemNotification } from '@hooks/useNotificationWebSocket';
import { Button } from '@/components/lumen/button';

interface SystemNotificationBannerProps {
  notification: SystemNotification | null;
  onDismiss: () => void;
}

const SystemNotificationBanner: React.FC<SystemNotificationBannerProps> = ({ notification, onDismiss }) => {
  const { t } = useLanguage();

  const handleClick = () => {
    onDismiss();
    // Force a hard reload bypassing cache (like Cmd+Shift+R) so
    // the user picks up the latest deployed assets.
    // Preserve existing query params (e.g. batchUUID) and only set/replace _t.
    const url = new URL(window.location.href);
    url.searchParams.set('_t', String(Date.now()));
    window.location.href = url.toString();
  };

  return (
    <AnimatePresence>
      {notification && (
        <motion.div
          initial={{ opacity: 0, y: -40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -40 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="fixed top-4 z-[10000] pointer-events-none flex justify-center"
          style={{ left: 88, right: 0 }}
        >
          <div className="pointer-events-auto flex items-center gap-3 rounded-card border border-label bg-raised px-5 py-3 shadow-overlay">
            {/* Icon */}
            <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-label/10 text-label">
              <Info className="size-4" aria-hidden />
            </div>

            {/* Message */}
            <p className="max-w-md font-medium text-fg text-sm">{notification.message}</p>

            {/* Action button */}
            <Button type="button" size="xs" className="ml-2" onClick={handleClick}>
              {t('lumen.frame.okayRefresh')}
            </Button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SystemNotificationBanner;
